import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Search, Archive, ArchiveRestore, X } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Shell, Empty } from "@/components/Shell";
import { RagBadge, RAG_OPTIONS } from "@/components/Rag";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const BLANK = {
  name: "", code: "", description: "", programme_id: "", portfolio_id: "", project_manager: "",
  project_owner: "", sponsor: "", business_owner: "", governance_framework: "", stage: "", rag: "Green",
  priority: "", department: "", start_date: "", end_date: "", percent_complete: 0, status: "Active",
};

const Select = ({ value, onChange, options, placeholder, testId }) => (
  <select
    data-testid={testId}
    value={value || ""}
    onChange={(e) => onChange(e.target.value)}
    className="h-9 w-full rounded-md border border-input bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
  >
    <option value="">{placeholder}</option>
    {options.map((o) => (
      <option key={o.value ?? o} value={o.value ?? o}>
        {o.label ?? o}
      </option>
    ))}
  </select>
);

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [lists, setLists] = useState({});
  const [portfolios, setPortfolios] = useState([]);
  const [programmes, setProgrammes] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [q, setQ] = useState("");
  const [ragFilter, setRagFilter] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const load = (archived = showArchived) =>
    api.get(`/projects?archived=${archived}`).then(({ data }) => setProjects(data));

  useEffect(() => {
    load(showArchived);
    api.get("/config/lists").then(({ data }) =>
      setLists(Object.fromEntries(data.map((l) => [l.key, l.values]))),
    );
    api.get("/portfolios").then(({ data }) => setPortfolios(data));
    api.get("/programmes").then(({ data }) => setProgrammes(data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showArchived]);

  useEffect(() => {
    if (params.get("new")) {
      setForm(BLANK);
      setOpen(true);
      setParams({});
    }
  }, [params, setParams]);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) return toast.error("Project name is required");
    try {
      const payload = { ...form, percent_complete: Number(form.percent_complete) || 0 };
      const { data } = await api.post("/projects", payload);
      const setup = data.framework_setup;
      toast.success(
        setup
          ? `Project created — ${setup.framework} applied${setup.stage ? ` at ${setup.stage}` : ""}${setup.risks_created ? `, ${setup.risks_created} standard risks added` : ""}`
          : "Project created",
      );
      setOpen(false);
      setForm(BLANK);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const archiveAction = async (p, action) => {
    try {
      await api.post(`/projects/${p.id}/${action}`);
      await load();
      toast.success(action === "archive" ? "Project archived — restore it any time" : "Project restored");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const pfFilter = params.get("portfolio") || "";
  const pgFilter = params.get("programme") || "";

  const filtered = projects.filter(
    (p) =>
      (!q || p.name.toLowerCase().includes(q.toLowerCase())) &&
      (!ragFilter || p.rag === ragFilter) &&
      (!pfFilter || p.portfolio_id === pfFilter) &&
      (!pgFilter || p.programme_id === pgFilter),
  );

  const pfName = (id) => portfolios.find((x) => x.id === id)?.name || "—";
  const pgName = (id) => programmes.find((x) => x.id === id)?.name || "—";

  return (
    <Shell
      testId="projects-page"
      title="Projects"
      subtitle={`${projects.length} ${showArchived ? "archived" : "project"} record${projects.length === 1 ? "" : "s"} in this workspace`}
      actions={
        <div className="flex items-center gap-2">
          <Button variant="outline" data-testid="toggle-archived-projects-btn" onClick={() => setShowArchived((v) => !v)} className="gap-2">
            <Archive size={15} /> {showArchived ? "Show Active" : "Show Archived"}
          </Button>
          <Button data-testid="new-project-btn" onClick={() => { setForm(BLANK); setOpen(true); }} className="gap-2" style={{ backgroundColor: "var(--brand-primary)" }}>
            <Plus size={15} /> New Project
          </Button>
        </div>
      }
    >
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-xs">
          <Search size={15} className="absolute left-2.5 top-2.5 text-slate-400" />
          <Input data-testid="project-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search projects…" className="pl-8" />
        </div>
        <div className="w-40">
          <Select testId="project-rag-filter" value={ragFilter} onChange={setRagFilter} options={RAG_OPTIONS} placeholder="All RAG" />
        </div>
        {(pfFilter || pgFilter) && (
          <button
            data-testid="clear-scope-filter-btn"
            onClick={() => setParams({})}
            className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-100"
          >
            {pfFilter ? `Portfolio: ${pfName(pfFilter)}` : `Programme: ${pgName(pgFilter)}`}
            <X size={13} className="text-slate-400" />
          </button>
        )}
      </div>

      {filtered.length === 0 ? (
        <Empty testId="projects-empty" text="No projects match. Create your first project record to get started." />
      ) : (
        <div className="panel overflow-x-auto" data-testid="projects-table">
          <table className="w-full min-w-[1000px] text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                {["Project", "Portfolio", "Programme", "Stage", "Owner", "PM", "Priority", "% Complete", "RAG", ""].map((h) => (
                  <th key={h} className="px-4 py-2.5 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((p) => (
                <tr
                  key={p.id}
                  data-testid={`project-row-${p.id}`}
                  onClick={() => navigate(`/projects/${p.id}`)}
                  className="cursor-pointer transition-colors hover:bg-slate-50"
                >
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-800">{p.name}</div>
                    <div className="text-xs text-slate-400">{p.code || "—"}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{pfName(p.portfolio_id)}</td>
                  <td className="px-4 py-3 text-slate-600">{pgName(p.programme_id)}</td>
                  <td className="px-4 py-3 text-slate-600">{p.stage || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{p.project_owner || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{p.project_manager || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{p.priority || "—"}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 w-20 overflow-hidden rounded-full bg-slate-100">
                        <div className="h-full rounded-full" style={{ width: `${p.percent_complete || 0}%`, backgroundColor: "var(--brand-primary)" }} />
                      </div>
                      <span className="text-xs text-slate-500">{p.percent_complete || 0}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-3"><RagBadge value={p.rag} testId={`project-rag-${p.id}`} /></td>
                  <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    {p.is_archived ? (
                      <button data-testid={`restore-project-${p.id}`} onClick={() => archiveAction(p, "restore")} aria-label="Restore project" title="Restore project" className="text-slate-400 hover:text-emerald-600">
                        <ArchiveRestore size={15} />
                      </button>
                    ) : (
                      <button data-testid={`archive-project-${p.id}`} onClick={() => archiveAction(p, "archive")} aria-label="Archive project" title="Archive project" className="text-slate-300 hover:text-amber-600">
                        <Archive size={15} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="project-dialog" className="max-h-[88vh] max-w-3xl overflow-y-auto">
          <DialogHeader><DialogTitle>New Project</DialogTitle>
            <DialogDescription>Create a project record with governance, ownership and schedule details.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="field-label">Project Name *</label>
              <Input data-testid="project-name-input" value={form.name} onChange={(e) => set("name")(e.target.value)} />
            </div>
            <div><label className="field-label">Project Code</label><Input data-testid="project-code-input" value={form.code} onChange={(e) => set("code")(e.target.value)} /></div>
            <div><label className="field-label">Portfolio</label><Select testId="project-portfolio-select" value={form.portfolio_id} onChange={set("portfolio_id")} options={portfolios.map((p) => ({ value: p.id, label: p.name }))} placeholder="Select portfolio" /></div>
            <div><label className="field-label">Programme</label><Select testId="project-programme-select" value={form.programme_id} onChange={set("programme_id")} options={programmes.map((p) => ({ value: p.id, label: p.name }))} placeholder="Select programme" /></div>
            <div><label className="field-label">Governance Framework</label><Select testId="project-governance-select" value={form.governance_framework} onChange={set("governance_framework")} options={lists.governance_framework || []} placeholder="Select" /></div>
            <div><label className="field-label">Stage / Gate</label><Select testId="project-stage-select" value={form.stage} onChange={set("stage")} options={lists.stage || []} placeholder="Select" /></div>
            <div><label className="field-label">Priority</label><Select testId="project-priority-select" value={form.priority} onChange={set("priority")} options={lists.priority || []} placeholder="Select" /></div>
            <div><label className="field-label">Department</label><Select testId="project-department-select" value={form.department} onChange={set("department")} options={lists.department || []} placeholder="Select" /></div>
            <div><label className="field-label">RAG Health</label><Select testId="project-rag-select" value={form.rag} onChange={set("rag")} options={RAG_OPTIONS} placeholder="Select" /></div>
            <div><label className="field-label">Project Manager</label><Input data-testid="project-pm-input" value={form.project_manager} onChange={(e) => set("project_manager")(e.target.value)} /></div>
            <div><label className="field-label">Project Owner</label><Input data-testid="project-owner-field-input" value={form.project_owner} onChange={(e) => set("project_owner")(e.target.value)} /></div>
            <div><label className="field-label">Exec Sponsor</label><Input data-testid="project-sponsor-input" value={form.sponsor} onChange={(e) => set("sponsor")(e.target.value)} /></div>
            <div><label className="field-label">Business Owner</label><Input data-testid="project-owner-input" value={form.business_owner} onChange={(e) => set("business_owner")(e.target.value)} /></div>
            <div><label className="field-label">Start Date</label><Input data-testid="project-start-input" type="date" value={form.start_date} onChange={(e) => set("start_date")(e.target.value)} /></div>
            <div><label className="field-label">End Date</label><Input data-testid="project-end-input" type="date" value={form.end_date} onChange={(e) => set("end_date")(e.target.value)} /></div>
            <div><label className="field-label">% Complete</label><Input data-testid="project-percent-input" type="number" min="0" max="100" value={form.percent_complete} onChange={(e) => set("percent_complete")(e.target.value)} /></div>
            <div className="sm:col-span-2"><label className="field-label">Description</label><Textarea data-testid="project-description-input" value={form.description} onChange={(e) => set("description")(e.target.value)} rows={3} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" data-testid="project-cancel-btn" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="project-save-btn" onClick={save} style={{ backgroundColor: "var(--brand-primary)" }}>Create Project</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}

export { Select };
